import React, { useEffect, useState, useCallback } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { createRoot } from '@wordpress/element';
import { getConnectionInfo, ConnectionContext, displayErrorMessage, displayLoader, getEndpoint } from './utils';
import { getStoredData } from "./utils/data-pulls";
import { Tab, TabsList, TabPanel, Tabs } from '@mui/base'; // https://mui.com/base-ui/react-tabs/
import { Sidebar } from "./components";
import { UpcomingGamesWidget } from "./components/widgets/upcomingGamesWidget";
import { AcaSelect } from "./components/forms/acaSelect";
import { Option } from '@mui/base/Option';
import { DateGrid } from "./components/forms/dateGrid";
import { SmallMatchBox } from "./components/smallMatchBox";
import { StandingsAndBracket } from "./components/tables/standingsAndBracket";
import { TeamLogo } from "./components/teamLogo";
import { NewsItem } from "./components/widgets/newsItem";
import { SocialWidget } from "./components/widgets/socialWidget";

const ClubApp = (props) => {
    const { 
        uid = '',
        name = '',
        postId = '',
        ...restProps 
    } = props;

    // Error and load handling
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isMatchesLoaded, setIsMatchesLoaded] = useState(false);

    // Connection Information
    const [connectionInfo, setConnectionInfo] = useState(null);

    // Setting data from API
    const [ data, setData ] = useState(null);
    const [ liveData, setLiveData ] = useState(null);

    const [ news, setNews ] = useState(null);

    const [ seasons, setSeasons ] = useState(null);
    const [ selectedSeason, setSelectedSeason ] = useState(null);

    const [ scheduleDates, setScheduleDates ] = useState(null);
    const [ selectedScheduleDate, setSelectedScheduleDate ] = useState(null);
    const [ matches, setMatches ] = useState(null);

    const [ standings, setStandings ] = useState(null);

    // Layout pieces
    const [ selectedTab, setSelectedTab ] = useState(1);

    /**
     * Get our connection info and stored data
     */
    useEffect(() => {

        // Get our connection info
        getConnectionInfo().then(
            (result) => {
                setConnectionInfo(result.data);
            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

        let args = [
            { 
                key: 'club',
                id: postId
            }
        ];

        // Get our stored data
        getStoredData(args).then(
            (result) => {
                // Set club data
                if ( result.data.club ) {
                    setData(result.data.club);
                    if ( result.data.club.hasOwnProperty('news') && result.data.club.news ) {
                        setNews(result.data.club.news);
                    }
                    // Get list of seasons from the stored club data
                    if ( result.data.club.hasOwnProperty('competitions') && result.data.club.competitions ) {
                        let competitionSeasons = [];
                        const competitions = result.data.club.competitions;
                        competitions.forEach((competition) => {
                            if ( !competitionSeasons.includes(competition.season) ) competitionSeasons.push(competition.season);
                        });
                        if ( competitionSeasons.length ) {
                            competitionSeasons.sort().reverse();
                            setSeasons(competitionSeasons);
                            setSelectedSeason(competitionSeasons[0]);
                        }
                    }

                }

                // Set loaded to true
                setIsLoaded(true);
                
            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

    }, []);


    /**
     * Get club data from the LIVE API
     */
    useEffect(() => {
        if (!connectionInfo) return;

        const clubEndpoint = getEndpoint('team', {
            teamId: uid,
            tenant: connectionInfo.tenant
        }, {
            organizationIdFilter: connectionInfo.organization_id
        }, connectionInfo.environment);
        
        fetch(clubEndpoint, {
            method: 'GET',
            headers: {
                'API_KEY': connectionInfo.api_key
            },
        })
          .then(res => res.json())
          .then(
            (result) => {
              if ( !result.hasOwnProperty('messages') ) {
                setLiveData(result);
              }
            },
            (error) => {
              setIsLoaded(true);
              setError(error);
              console.log(error);
            }
          );

    }, [connectionInfo]);


    /**
     * After getting our season, get schedules and standings
     */
    useEffect(() => {
        if ( !selectedSeason ) return;

        let args = [
            {
                key: 'scheduleDates',
                type: 'clubs',
                season: selectedSeason,
                clubs: [uid]
            },
            {
                key: 'clubStandings',
                season: selectedSeason,
                club: postId
            }
        ];

        // Get our stored data
        getStoredData(args).then(
            (result) => {

                // Set schedule
                if ( result.data.scheduleDates ) {
                    setScheduleDates(result.data.scheduleDates);
                } else {
                    setScheduleDates(null);
                    setSelectedScheduleDate(null);
                }

                // Set clubStandings
                if ( result.data.clubStandings ) {
                    setStandings(result.data.clubStandings);
                } else {
                    setStandings(null);
                }

                // Set loaded
                setIsLoaded(true);

            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

    }, [selectedSeason]);



    /**
     * Get matches for the specific day
     */
    useEffect(() => {
        if ( !selectedScheduleDate || !selectedSeason ) return;
        setIsMatchesLoaded(false);

        // Turn date into something parseable by PHP
        const year = selectedScheduleDate.toLocaleString("default", { year: "numeric", timeZone: 'UTC' });
        const month = selectedScheduleDate.toLocaleString("default", { month: "2-digit", timeZone: 'UTC' });
        const day = selectedScheduleDate.toLocaleString("default", { day: "2-digit", timeZone: 'UTC' });
        const formattedDate = year + "-" + month + "-" + day;

        let args = [
            {
                key: 'matches',
                type: 'clubs',
                date: formattedDate,
                season: selectedSeason,
                competition: 'all',
                clubs: [uid]
            }
        ];

        // // Get our stored data
        getStoredData(args).then(
            (result) => {

                // Set Matches
                if ( result.data.matches ) {

                    /**
                     * Sort the matches into associative arrays by competition
                     */
                    let sortedMatches = {};
                    result.data.matches.forEach((match, i) => {
                        const competition = match.competition;

                        // Create the competition in the array if it doesn't already exist
                        if ( !sortedMatches.hasOwnProperty(competition.id) ) {
                            sortedMatches[competition.id] = {
                                name: competition.name,
                                matches: []
                            };
                        }

                        // Add the match to its competition
                        sortedMatches[competition.id].matches.push(match);

                    });

                    if ( result.data.matches.length == 0 ) {
                        setMatches(null);
                    } else {
                        setMatches(sortedMatches);
                    }

                }
                setIsMatchesLoaded(true);

            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

    }, [selectedScheduleDate, selectedSeason]);

    /**
     * Handle tab changes
     */
    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };


    /**
     * Handle season changes
     */
    const handleSeasonChange = (event, value) => {
        setSelectedSeason(value);
    }


    /**
     * Handle selected date changes
     */
    const handleDateChange = (date = null) => {
        if ( !date ) return;
        setSelectedScheduleDate(date);
    }


    // Bail if we don't have data yet
    if (!isLoaded ) {
        // return displayLoader('acaLoader--main');
    }

    // Display an error message if there's a problem
    if ( error ) {
        console.log(error);
        return displayErrorMessage();
    }

    return (
        <ConnectionContext.Provider value={connectionInfo}>

            <Tabs className="acaPage acaPage--club" slots={{ root: 'section' }} value={ selectedTab }  onChange={handleTabChange}>
                { /** Header */ }
                <div className="acaHeader acaHeader--light acaHeader--club">
                    <div className="acaWrapper acaHeader__inner">
                        { liveData && liveData.hasOwnProperty('picture') && liveData.picture ? (
                            <div className="acaHeader__logo">
                                <TeamLogo defaultPictureId={liveData.picture} />
                            </div>
                        ) : '' }
                        <div className="acaHeader__main">
                            <h1 className="acaH1 acaHeader__title">{name}</h1>
                            <p className="acaH3 acaHeader__desc">Select a season to view schedules and standings</p>
                        </div>

                        <div className="acaHeader__aside">
                            { /** Seasons Select */ }
                            { seasons && seasons.length ? (
                                <AcaSelect extraClasses="acaSelect--fullWidth" onChange={handleSeasonChange} defaultValue={selectedSeason ? selectedSeason : ''} id="schedule-season" name="schedule-season">
                                    { seasons.map((season, i) => (
                                        <Option key={i} value={season}>{season}</Option>
                                    ))}
                                </AcaSelect>
                            ) : '' }
                        </div>
                        
                        <TabsList className="acaHeader__tabs acaLargeTabs" >
                            <Tab value={1}>Club Information</Tab>
                            { scheduleDates  ? (
                                <Tab value={2}>Game Schedule</Tab>
                            ) : '' }
                            { standings ? (
                                <Tab value={3}>
                                    Standings
                                </Tab>
                            ) : '' }
                        </TabsList>
                    </div>
                </div>

                { /** Main Content */ }
                
                { /** Club Information */ }
                <TabPanel value={1}>
                    
                    <section className="acaPage acaPage--withSidebar">

                        <div className="acaMain">

                            { /** News */ }
                            { news ? (
                                <ErrorBoundary fallback={displayErrorMessage('No news currently available')}> 
                                    <div className="acaWidget acaWidget--news">

                                        <div className="acaWidget__header">
                                            <div className="acaWidget__headerMain">
                                                <h2 className="acaH2 acaWidget__title">Club News</h2>
                                            </div>
                                            { data && data.hasOwnProperty('newsLink') && data.newsLink ? (
                                                <div className="acaWidget__headerAside">
                                                    <a href={data.newsLink} className="acaBtn acaBtn--solid acaBtn--small acaBtn--grey acaBtn--arrowRightIcon">See all club news</a>
                                                </div>
                                            ) : '' }
                                        </div>
                                        
                                        <div className="acaWidget__inner">
                                            { news.map((item, i) => (
                                                <NewsItem key={i} data={item} />
                                            )) }
                                        </div>

                                    </div>
                                </ErrorBoundary>
                            ) : '' }
                            

                            {/** Club contact information */ }
                            { data && data.hasOwnProperty('contacts') && data.contacts ? (
                                <div className="clubContacts paddingTop--small">
                                    <h3 className="acaH2 clubContacts__title">Club contact information</h3>
                                    <div className="clubContacts__inner" dangerouslySetInnerHTML={{ __html: data.contacts }}>
                                    </div>
                                </div>
                            ) : '' }


                        </div>

                        <Sidebar>

                            <ErrorBoundary fallback={displayErrorMessage()}>
                                <UpcomingGamesWidget type="team" team={uid} />
                            </ErrorBoundary>

                            { liveData && liveData.hasOwnProperty('facility') && liveData.facility ? (
                                <div className="acaWidget acaWidget--homeFacility">
                                    <div className="acaWidget__header">
                                        <div className="acaWidget__headerMain">
                                            <h2 className="acaH4 acaWidget__title">
                                                Home Facility
                                            </h2>
                                        </div>
                                    </div>
                                    <div className="acaWidget__inner">
                                        <div className="acaWidgetItem">
                                            <h5 className="acaH5--alt acaWidgetItem__title">{ liveData.facility.name }</h5>
                                            { liveData.facility.hasOwnProperty('address') ? (
                                                <div className="acaWidgetItem__content">
                                                    { liveData.facility.address }
                                                </div>
                                            ) : '' }
                                            
                                        </div>
                                    </div>
                                    
                                </div>
                            ) : '' }

                            { liveData ? (
                                <SocialWidget 
                                    twitter={ liveData.hasOwnProperty('twitter') && liveData.twitter ? liveData.twitter : null }
                                    instagram={ liveData.hasOwnProperty('instagram') && liveData.instagram ? liveData.instagram : null }
                                    website={ liveData.hasOwnProperty('website') && liveData.website ? liveData.website : null }
                                />
                            ) : '' }

                        </Sidebar>

                    </section>
                    
                </TabPanel>

                { /** Game Schedule */ }
                <TabPanel value={2}>
                    <ErrorBoundary fallback={displayErrorMessage()}> 
                        <DateGrid dates={scheduleDates} onChange={handleDateChange}></DateGrid>
                        <div className="acaMain">
                            { isMatchesLoaded ? 
                                selectedScheduleDate && matches ? (
                                    <>
                                        <h2 className="acaH1 acaScheduleGrid__title">{ selectedScheduleDate.toLocaleString("en-US", { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }) }</h2>
    
                                        {
                                        Object.keys(matches).map((key, i) => (
                                                <div key={i} className="acaScheduleGrid__section">
                                                    <h3 className="acaH2 acaScheduleGrid__subtitle">{ matches[key].name }</h3>
                                                    <div className="acaScheduleGrid">
                                                        { matches[key].matches.map((match, n) => (
                                                            <SmallMatchBox key={n} data={match} />
                                                        )) }
                                                    </div>
                                                </div>
                                            ) )
                                        }
    
                                    </>
                                ) : (
                                    displayErrorMessage('No recent matches to show. Check back later, or see the schedule and standings page for upcoming games.')
                                )
                            : displayLoader('acaLoader--main') }
                        </div>
                    </ErrorBoundary>
                </TabPanel>

                { /** Standings */ }
                <TabPanel value={3}>
                    <ErrorBoundary fallback={displayErrorMessage()}> 
                        { standings ? (
                            standings.map((el, i) => (
                                <div key={i}>
                                    <StandingsAndBracket season={el.season} standings={el.standings} showTitle={true} title={el.name} highlight={uid} />
                                </div>
                            ))
                        ) : '' }
                    </ErrorBoundary>
                </TabPanel>

            </Tabs>
        </ConnectionContext.Provider>
    );
}

export default ClubApp;

// Render Match Page
const appContainer = document.getElementById('clubApp');
const root = createRoot(appContainer);
const name = appContainer.dataset.name;
const postId = appContainer.dataset.id;
const uid = appContainer.dataset.uid;
root.render(<ClubApp postId={postId} uid={uid} name={name} />);